import conversationModule from '@/store/modules/conversation.module'
import answerModule from '@/store/modules/answer.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import templateModule from '@/store/modules/template.module'
import randomUtils from '@/utils/random.utils'
import stateTreeUtils from '../utils/state-tree.utils'
import {
  ACTION_TYPES,
  ANSWER_END,
  EXPRESSION_TYPES,
  INPUT_TYPES,
  STATE_ID,
  STATE_TYPES
} from '@/utils/bot-json.constants'
import propEq from 'ramda/src/propEq'
import forEachObjIndexed from 'ramda/src/forEachObjIndexed'
import clone from 'ramda/src/clone'
import head from 'ramda/src/head'
import filter from 'ramda/src/filter'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import last from 'ramda/src/last'
import { checkIsObject, checkIsString, checkValue } from '@/utils/preconditions'
import PreviewController from './preview.controller'
import { NODE_TYPES, EDGE_TRANSITION_TYPES } from '@/utils/state-tree.generator'
import previewUtils from '@/utils/preview.utils'

class AnswerController extends PreviewController {

  addBubble(node, isInsideCondition, templateId, answerEndType = null) {
    checkIsObject(node, 'node')
    this.resolveEmptyConversation(node)
    super.addBubble(node, isInsideCondition, templateId)
  }

  resolveEmptyConversation(node) {
    if(node.stateId === STATE_ID.ROOT && node.type === NODE_TYPES.EMPTY) {
      conversationModule.removeState(node.stateId)
    }
  }

  useTemplate(rootJson, currentStateId, template, node, templateRefSuffix = randomUtils.randomString(6)) {
    checkIsObject(rootJson, 'rootJson')
    checkIsString(currentStateId, 'currentStateId')
    checkIsObject(template, 'template')
    checkIsString(templateRefSuffix, 'templateRefSuffix')

    templateModule.addToTemplateRefsCache(template)
    conversationModule.addTemplate(template)

    const templateStates = clone(template.conversationJson.states)
    const isEmptyConversationJson = rootJson.states.root?.type === STATE_TYPES.EMPTY
    const isPostbackNode = node && stateTreeUtils.nodeIsPostback(node)
    const stateExists = conversationModule.stateExists(currentStateId)

    forEachObjIndexed((templateState, templateStateId) => {
      const templateTransitions = templateState.transitions.map(transition => {
        const templateTransition = clone(transition)
        if ([STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.REF_GLOBAL_IDLE].includes(transition.state) ||
          transition.state.startsWith('ref:answer:')
        ) {
          return templateTransition
        }

        templateTransition.state = transition.state + templateRefSuffix
        return templateTransition
      })

      const addDefaultTransition = isEmpty(templateState.transitions) || !templateState.transitions.some(transition => {
        return (
          [STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.REF_GLOBAL_IDLE].includes(transition.state) ||
          [INPUT_TYPES.NONE, INPUT_TYPES.USER_INPUT].includes(transition.condition.input)
        )
      })

      const newStateId = (isEmptyConversationJson || !stateExists) && templateStateId === STATE_ID.ROOT ?
        currentStateId :
        templateStateId + templateRefSuffix

      conversationModule.createTemplateRefState(conversationModule.getType(), conversationModule.getId(), newStateId, template.id, templateStateId,
        templateRefSuffix, templateTransitions, addDefaultTransition, templateState.requestOutcome)

      // find postbacks in template actions and add them to rootJson
      if (!templateState.actions) return
      for (const actionId of templateState.actions) {
        const action = template.conversationJson.actions[actionId]
        if (action.type === ACTION_TYPES.OUTPUT && action.content.type === 'structure') {
          // TODO structure can be an array
          for (const postbackButton of filter(propEq('type', 'postback'), action.content.payload.buttons)) {
            const postbackValue = template.conversationJson.postbacks[postbackButton.payload]
            const resolvedPostbackValue = postbackValue.startsWith('ref:') ? postbackValue : postbackValue + templateRefSuffix
            conversationModule.addPostback(postbackButton.payload + templateRefSuffix, resolvedPostbackValue)
          }
        }
      }
    }, templateStates)

    if (isPostbackNode && !stateExists) {
      conversationModule.addPostback(node.parent.transition.postback, currentStateId)
      return currentStateId
    }

    if (isEmptyConversationJson || !stateExists) {
      if (node && stateTreeUtils.nodeIsQuickReply(node)) {
        conversationModule.updateStateTransitionStateIdByIndex(node.parent.node.stateId, currentStateId, node.parent.transition.index)
      }

      return currentStateId
    }

    const templateRefStateId = STATE_ID.ROOT + templateRefSuffix
    this.replaceTransition(currentStateId, templateRefStateId)

    return templateRefStateId
  }

  removeTemplateRef(rootJson, stateTree, selectedStateId) {
    checkIsObject(rootJson, 'rootJson')
    checkIsObject(stateTree, 'stateTree')
    checkIsString(selectedStateId, 'selectedStateId')
    checkValue(rootJson.states[selectedStateId].type, 'templateRef', 'rootJson.states[selectedStateId].type')

    const initRootJson = clone(rootJson)
    const itemsToRemove = stateTreeUtils.findTemplateRefToRemove(stateTree, rootJson, selectedStateId)
    const { fromNode, toNode } = itemsToRemove.moveTransitions

    if (toNode && fromNode) {
      if (this._isEmptyConditionCase(toNode, fromNode, itemsToRemove.firstTemplateNode)) {
        this.replaceTransition(toNode.stateId, toNode.conditionEndStateId)
      } else {
        const toStateId = this.getTransitionsStateId(toNode.stateId)
        const fromTransitions = conversationModule.getStateTransitions(this.getTransitionsStateId(fromNode.stateId))
        if (isEmpty(fromTransitions)) {
          conversationModule.replaceLastTransitionWithFindIntent(toStateId)
        } else {
          const updatedTransitions = this.moveTransitions(conversationModule.getStateTransitions(toStateId), fromTransitions[0], itemsToRemove.states[0])
          conversationModule.setStateTransitions(toStateId, updatedTransitions)
        }

        const sectionFocus = previewUtils.getSectionFocusFromTransition(itemsToRemove.firstTemplateNode.parent?.transition)
        stateTreeUtils.selectNode(toNode, sectionFocus)
      }
    }

    const statesToRemove = new Set(itemsToRemove.states)
    const isTransitionToIdle = answerModule.isAnswerEditor() ? conversationModule.getFirstStateTransition(last(itemsToRemove.states))?.state === STATE_ID.REF_GLOBAL_IDLE : false
    for (const stateId of statesToRemove) {
      conversationModule.removeState(stateId)
      if (stateId === STATE_ID.ROOT && itemsToRemove.nextStateId) {
        conversationModule.renameState(itemsToRemove.nextStateId, STATE_ID.ROOT)
      }
      if (stateId === itemsToRemove.postbackRoot && itemsToRemove.nextStateId) {
        conversationModule.renameState(itemsToRemove.nextStateId, itemsToRemove.postbackRoot)
      }
    }
    for (const postbackKey of itemsToRemove.postbacks) {
      conversationModule.removePostback(postbackKey)
    }

    if (!toNode && !itemsToRemove.nextStateId && itemsToRemove.firstTemplateNode.stateId !== itemsToRemove.postbackRoot) {
      if (answerModule.isAnswerEditor()) {
        conversationModule.setEmptyJson(isTransitionToIdle)
      }
      stateTreeUtils.selectEmpty()
    }

    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  addConditionSection(node, templateId) {
    checkIsObject(node, 'node')
    this.resolveEmptyConversation(node)
    const transitions = [{
      state: STATE_ID.REF_GLOBAL_FIND_INTENT,
      condition: {
        input: INPUT_TYPES.USER_INPUT,
        expression: { true: {} }
      }
    }]
    this.addConditionSectionWithTransitions(node, transitions)
  }

  addWaitForUserInput(node, isInsideCondition, templateId = null) {
    checkIsObject(node, 'node')

    const stateId = node.stateId
    const transitionStateId = super.getTransitionsStateId(stateId, isInsideCondition)
    const nextStateId = this.generateId()
    conversationModule.replaceLastTransitionWithExpressionTrue(transitionStateId, nextStateId, INPUT_TYPES.USER_INPUT)
    chatBubbleModule.selectWaitForInput(transitionStateId, nextStateId)

    return { transitionStateId, nextStateId }
  }

  addPostback(stateId, postbackKey, postbackValue, templateId = null, postbackExists = false) {
    conversationModule.addPostback(postbackKey, postbackValue)
  }

  addRemoteBubble(node, isInsideCondition, templateId = null, answerEndType = null) {
    checkIsObject(node, 'node')
    this.resolveEmptyConversation(node)
    super.addRemoteBubble(node, isInsideCondition, templateId, answerEndType)
  }

  addChatGPTBubble(node, isInsideCondition, templateId = null, answerEndType = null) {
    checkIsObject(node, 'node')
    this.resolveEmptyConversation(node)
    super.addChatGPTBubble(node, isInsideCondition, templateId, answerEndType)
  }

  updateAnswerEnd(stateId, answerEndType, bubbleSelected, answerId, transitionIndex = -1) {
    const actualStateId = this.getStateIdForAnswerEnd(stateId, bubbleSelected.isCondition)

    switch (answerEndType) {
      case ANSWER_END.FIND_INTENT:
        this.replaceTransition(actualStateId, STATE_ID.REF_GLOBAL_FIND_INTENT)
        break
      case ANSWER_END.IDLE:
        if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
          conversationModule.addPostback(bubbleSelected.stateId, STATE_ID.REF_GLOBAL_IDLE)
        } else if (transitionIndex !== -1) {
          conversationModule.updateStateTransitionStateIdByIndex(actualStateId, STATE_ID.REF_GLOBAL_IDLE, transitionIndex)
        } else {
          this.replaceTransition(actualStateId, STATE_ID.REF_GLOBAL_IDLE)
        }
        break
      case ANSWER_END.WAIT_INPUT_THEN_IDLE:
        this.replaceTransitionAndInputType(actualStateId, STATE_ID.REF_GLOBAL_IDLE, INPUT_TYPES.USER_INPUT)
        break
      case ANSWER_END.CONTINUE_CONDITION:
        this.replaceTransition(actualStateId, bubbleSelected.conditionEndStateId)
        break
      case ANSWER_END.CONTINUE_OR_FIND_INTENT: {
        // PRO-367: two transitions, order matters — the AI judge decides first whether the customer stays in this
        // answer (self-loop re-runs it), otherwise it falls through to regular intent recognition.
        // Keep quick-reply transitions ahead of the pair (same layout as other answer ends).
        const qrTransitions = conversationModule.getStateTransitions(actualStateId)
          .filter(transition => transition.condition?.input === INPUT_TYPES.QUICK_REPLY)
        conversationModule.setStateTransitions(actualStateId, [
          ...qrTransitions,
          {
            state: actualStateId,
            condition: { input: INPUT_TYPES.AI_JUDGE, expression: { [EXPRESSION_TYPES.CONTINUE_CONVERSATION]: {} } }
          },
          {
            state: STATE_ID.REF_GLOBAL_FIND_INTENT,
            condition: { input: INPUT_TYPES.USER_INPUT, expression: { [EXPRESSION_TYPES.TRUE]: {} } }
          }
        ])
        break
      }
      case ANSWER_END.CONTINUE_ANSWER:
        const refAnswerId = 'ref:answer:' + answerId
        if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
          conversationModule.addPostback(bubbleSelected.stateId, refAnswerId)
        } else if (transitionIndex !== -1) {
          conversationModule.updateStateTransitionStateIdByIndex(actualStateId, refAnswerId, transitionIndex)
        } else {
          this.replaceTransition(actualStateId, refAnswerId)
        }
        break
      default:
        break
    }
  }

  deleteAnswer(node) {
    const initRootJson = clone(conversationModule.getRootJson())
    this.removeSubtree(node)
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeBubble(node, templateId) {
    const initRootJson = clone(conversationModule.getRootJson())
    super.removeBubble(node, templateId)

    const childNode = last(node.children)?.node
    const updatedRootJson = clone(conversationModule.getRootJson())
    if (isEmpty(updatedRootJson.states) && answerModule.isAnswerEditor()) {
      conversationModule.setEmptyJson(childNode?.type === NODE_TYPES.IDLE)
    }
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeCaseFromCondition(conditionStateId, caseIndex) {
    const initRootJson = clone(conversationModule.getRootJson())
    super.removeCaseFromCondition(conditionStateId, caseIndex)
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeConditionSection(node) {
    const initRootJson = clone(conversationModule.getRootJson())
    super.removeConditionSection(node)
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removePostback(postbackKey) {
    const initRootJson = clone(conversationModule.getRootJson())
    super.removePostback(postbackKey)
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId = null) {
    const initRootJson = clone(conversationModule.getRootJson())
    this._removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId)
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeQuickReplies(qrStateIds, qrParentStateId, qrParentStateActionId, templateId = null) {
    const initRootJson = clone(conversationModule.getRootJson())
    qrStateIds.forEach(qrStateId => this._removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId))
    templateModule.deleteFromTemplateRefsCache(initRootJson)
  }

  removeNode(node) {
    super.removeNode(node)

    // special handling when we're removing the first state of a template
    // in order to not break anything, we have to rename the next state
    if ((isNil(node.parent) || node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) && node.children) {
      const nextState = node.children.find((childEdge) => {
        return ![EDGE_TRANSITION_TYPES.POSTBACK, EDGE_TRANSITION_TYPES.QUICK_REPLY].includes(childEdge.transition.type) &&
          [NODE_TYPES.OUTPUT, NODE_TYPES.CONDITION, NODE_TYPES.REMOTE, NODE_TYPES.CHAT_GPT, NODE_TYPES.SAVE_CONTEXT].includes(childEdge.node.type)
      })
      if (nextState) {
        // HACK: we cannot have a saveContext state as the 1st one, have to remove it
        if(nextState.node.type === NODE_TYPES.SAVE_CONTEXT) {
          conversationModule.renameState(nextState.node.children[0].node.stateId, node.stateId)
          conversationModule.removeState(nextState.node.stateId)
          return
        }
        if(isNil(node.parent)) {
          conversationModule.renameState(nextState.node.stateId, node.stateId)
        }
      }
    }
  }

  _isEmptyConditionCase(toNode, fromNode, firstTemplateNode) {
    if (toNode.type !== NODE_TYPES.CONDITION) return false

    const conditionSectionTransitions = conversationModule.getStateTransitions(toNode.stateId)
    const isFirstStateRemoved = conditionSectionTransitions.some(transition => transition.state === firstTemplateNode.stateId)
    if (!isFirstStateRemoved) return false

    const firstChild = head(fromNode.children)
    return firstChild.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY ||
      [NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.EMPTY].includes(firstChild.node.type)
  }
}

export default new AnswerController()
